import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { IconCamera, IconX, IconLoader2, IconUpload } from '@tabler/icons-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { invokeEdgeFunctionSafely } from '@/lib/supabase-functions-wrapper';
import { componentLogger } from '@/lib/logging';

interface ProfilePictureUploadProps {
  userId?: string | undefined;
  existingPictureId?: string | undefined;
  currentImage?: {
    tempStoragePath?: string;
    fileName?: string;
  } | undefined;
  onImageUploaded: (_imageData: {
    tempStoragePath: string;
    fileName: string;
    mimeType: string;
    sizeBytes: number;
  }) => void;
  onImageRemoved: () => void;
  disabled?: boolean | undefined;
  userInitials?: string | undefined;
}

const MAX_IMAGE_SIZE_MB = 10;
const MAX_IMAGE_SIZE_BYTES = MAX_IMAGE_SIZE_MB * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

export function ProfilePictureUpload({
  userId,
  existingPictureId,
  currentImage: _currentImage,
  onImageUploaded,
  onImageRemoved,
  disabled = false,
  userInitials = 'U',
}: ProfilePictureUploadProps) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [existingImageUrl, setExistingImageUrl] = useState<string | null>(null);
  const [fetchingImage, setFetchingImage] = useState(false);
  const [_fetchError, setFetchError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Fetch existing profile picture when existingPictureId is provided
  useEffect(() => {
    let mounted = true;

    const fetchExistingImage = async () => {
      if (!existingPictureId) {
        setExistingImageUrl(null);
        setFetchingImage(false);
        setFetchError(false);
        return;
      }

      try {
        setFetchingImage(true);
        setFetchError(false);

        const { data, error: fetchError } = await invokeEdgeFunctionSafely('get-download-url-for-attachment', {
          body: { attachmentId: existingPictureId }
        });

        if (!mounted) return;

        if (fetchError || !data) {
          componentLogger.error('profile-picture-upload', 'Failed to fetch existing profile picture URL', {
            userId,
            existingPictureId,
            error: fetchError
          });
          setFetchError(true);
          return;
        }

        const typedData = data as { presignedUrl: string };
        setExistingImageUrl(typedData.presignedUrl);
        componentLogger.debug('profile-picture-upload', 'Existing profile picture loaded', { userId, existingPictureId });
      } catch (err) {
        if (!mounted) return;
        componentLogger.error('profile-picture-upload', 'Error fetching existing profile picture URL', {
          userId,
          existingPictureId,
          error: err
        });
        setFetchError(true);
      } finally {
        if (mounted) {
          setFetchingImage(false);
        }
      }
    };

    fetchExistingImage();

    return () => {
      mounted = false;
    };
  }, [existingPictureId, userId]);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setErrorMessage(null);

    // Validate file type
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      const message = t('profile.invalid_file_type');
      setErrorMessage(message);
      componentLogger.error('profile-picture-upload', 'Invalid file type', { type: file.type });
      return;
    }

    // Validate file size
    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      const message = t('profile.picture_too_large', { maxSize: MAX_IMAGE_SIZE_MB });
      setErrorMessage(message);
      componentLogger.error('profile-picture-upload', 'File too large', { size: file.size });
      return;
    }

    // Create preview
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);

    setUploading(true);
    setUploadProgress(0);

    try {
      // Get presigned URL
      const { data: presignedData, error: presignedError } = await invokeEdgeFunctionSafely('request-temporary-upload-url', {
        body: {
          fileName: file.name,
          mimeType: file.type,
          fileSize: file.size,
        },
      });

      if (presignedError) {
        throw new Error((presignedError as Error).message || 'Failed to get presigned URL');
      }

      const typedPresignedData = presignedData as { presignedUrl: string; tempStoragePath: string };
      if (!typedPresignedData?.presignedUrl) {
        throw new Error('Failed to get upload URL');
      }

      // Upload file with progress tracking
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const progress = Math.round((e.loaded / e.total) * 100);
            setUploadProgress(progress);
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status === 200) {
            resolve();
          } else {
            reject(new Error(`Upload failed with status: ${xhr.status}`));
          }
        });

        xhr.addEventListener('error', () => {
          reject(new Error('Upload failed'));
        });

        xhr.open('PUT', typedPresignedData.presignedUrl);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.send(file);
      });

      // Notify parent component
      onImageUploaded({
        tempStoragePath: typedPresignedData.tempStoragePath,
        fileName: file.name,
        mimeType: file.type,
        sizeBytes: file.size,
      });

      componentLogger.debug('profile-picture-upload', 'Profile picture uploaded successfully', {
        userId,
        fileName: file.name,
      });
    } catch (error) {
      const message = t('profile.upload_error');
      setErrorMessage(message);
      componentLogger.error('profile-picture-upload', 'Upload failed', { error });
      // Clean up preview on error
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
      }
    } finally {
      setUploading(false);
      setUploadProgress(0);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveImage = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setExistingImageUrl(null);
    setErrorMessage(null);
    onImageRemoved();
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const hasImage = previewUrl || existingImageUrl;
  const displayUrl = previewUrl || existingImageUrl || undefined;

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium">{t('profile.profile_picture')}</label>
      <div className="flex items-start gap-4">
        <input
          ref={fileInputRef}
          type="file"
          accept={ALLOWED_IMAGE_TYPES.join(',')}
          onChange={handleFileSelect}
          className="hidden"
          disabled={disabled || uploading}
        />

        {/* Avatar Preview */}
        <div className="relative">
          <Avatar className="h-24 w-24">
            {fetchingImage ? (
              <AvatarFallback>
                <IconLoader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </AvatarFallback>
            ) : displayUrl ? (
              <AvatarImage src={displayUrl} alt={t('profile.profile_picture')} />
            ) : (
              <AvatarFallback className="text-lg">
                {userInitials}
              </AvatarFallback>
            )}
          </Avatar>

          {/* Remove button overlay when image exists */}
          {hasImage && !uploading && (
            <Button
              type="button"
              variant="destructive"
              size="icon"
              className="absolute -top-2 -right-2 h-6 w-6 rounded-full shadow-md"
              onClick={handleRemoveImage}
              disabled={disabled}
              aria-label={t('profile.remove_picture')}
            >
              <IconX className="h-3 w-3" />
            </Button>
          )}

          {/* Upload progress overlay */}
          {uploading && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full">
              <div className="text-white text-xs font-medium">{uploadProgress}%</div>
            </div>
          )}
        </div>

        {/* Upload/Change Button */}
        <div className="flex flex-col gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleButtonClick}
            disabled={disabled || uploading}
            className="w-full"
          >
            {uploading ? (
              <>
                <IconLoader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('profile.upload_in_progress')}
              </>
            ) : hasImage ? (
              <>
                <IconCamera className="mr-2 h-4 w-4" />
                {t('profile.change_picture')}
              </>
            ) : (
              <>
                <IconUpload className="mr-2 h-4 w-4" />
                {t('profile.upload_picture')}
              </>
            )}
          </Button>

          <p className="text-xs text-muted-foreground">
            {t('profile.drop_image_here')}
            <br />
            {`Max ${MAX_IMAGE_SIZE_MB}MB`}
          </p>

          {errorMessage && (
            <p className="text-xs text-destructive">{errorMessage}</p>
          )}
        </div>
      </div>
    </div>
  );
}
